import { useMemo } from "react"

import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import {
  ADD_TEAM_MEMBERS_OVERLAY,
  DELETE_PROJECT_OVERLAY,
  DUPLICATE_PROJECT_OVERLAY,
  EDIT_PROJECT_OVERLAY,
  MANAGE_PROJECT_TEAM_OVERLAY,
  MUSTERHEFT_EXPORT_OVERLAY,
  NEW_FLOOR_OVERLAY,
  NEW_PRODUCT_POSITION_OVERLAY,
  NEW_ROOM_OVERLAY,
  PROJECT_ATTACHMENTS_OVERLAY,
  PROJECT_GAEB_OVERLAY,
  PROJECT_HISTORY_OVERLAY,
  PROJECT_PDF_OVERLAY,
  SHARE_OVERLAY,
  TEAM_LEAVE_OVERLAY
} from "shared/context/overlay/types"
import { useEnhanceCallbacks } from "shared/lib/useEnhanceCallbacks"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"

import { useNavigateToProject } from "entities/projects/model/useNavigateToProject"
import { NewFloorOptions } from "widgets/floor/new-floor-overlay/NewFloorOverlayConnector"
import { NewProductPositionOptions } from "widgets/product-position/new-product-position-overlay/NewProductPositionOverlayConnector"
import { NewRoomOptions } from "widgets/room/new-room-overlay/NewRoomOverlayConnector"

import { ProjectActionHandlers } from "../types/ProjectActionHandlers"

export interface UseProjectActionHandlersArgs {
  onAction?: () => void
}

export function useProjectActionHandlers(
  args?: UseProjectActionHandlersArgs
): ProjectActionHandlers {
  const onAction = args?.onAction
  const navigate = useNavigateToProject(null)
  const { updateProfile: updateNeoletterProfile } = useNeoletterUserProfile()

  const { openOverlay: openDeleteProjectOverlay } = useOverlay(
    DELETE_PROJECT_OVERLAY
  )
  const { openOverlay: openNewProductPositionOverlay } = useOverlay(
    NEW_PRODUCT_POSITION_OVERLAY
  )
  const { openOverlay: openDuplicateProjectOverlay } = useOverlay(
    DUPLICATE_PROJECT_OVERLAY
  )

  const { openOverlay: openNewRoomOverlay } = useOverlay(NEW_ROOM_OVERLAY)

  const { openOverlay: openNewFloorOverlay } = useOverlay(NEW_FLOOR_OVERLAY)

  const { openOverlay: openShareOverlay } = useOverlay(SHARE_OVERLAY)

  const { openOverlay: openProjectGaebOverlay } =
    useOverlay(PROJECT_GAEB_OVERLAY)

  const { openOverlay: openProjectPdfOverlay } = useOverlay(PROJECT_PDF_OVERLAY)

  const { openOverlay: openMusterheftExportOverlay } = useOverlay(
    MUSTERHEFT_EXPORT_OVERLAY
  )

  const { openOverlay: openProjectAttachmentsOverlay } = useOverlay(
    PROJECT_ATTACHMENTS_OVERLAY
  )

  const { openOverlay: openCreateProjectTeamMembersOverlay } = useOverlay(
    ADD_TEAM_MEMBERS_OVERLAY
  )

  const { openOverlay: openEditProjectOverlay } =
    useOverlay(EDIT_PROJECT_OVERLAY)

  const { openOverlay: openManageProjectTeamOverlay } = useOverlay(
    MANAGE_PROJECT_TEAM_OVERLAY
  )
  const { openOverlay: openTeamLeaveOverlay } = useOverlay(TEAM_LEAVE_OVERLAY)

  const { openOverlay: openProjectHistoryOverlay } = useOverlay(
    PROJECT_HISTORY_OVERLAY
  )

  const handleOpenProject = useEvent((projectID: string) => {
    navigate({ id: projectID })
  })

  const handleEditProject = useEvent((projectID: string) => {
    openEditProjectOverlay({ projectID })
  })

  const handleAddClarificationRequest = useEvent((projectID: string) => {
    openEditProjectOverlay({ projectID })
  })

  const handleDeleteProject = useEvent((projectID: string) => {
    openDeleteProjectOverlay({ projectID })
  })

  const handleNewProductPosition = useEvent(
    (projectID: string, options?: NewProductPositionOptions) => {
      openNewProductPositionOverlay({ projectID, ...options })
    }
  )

  const handleNewRoom = useEvent(
    (projectID: string, options?: NewRoomOptions) => {
      openNewRoomOverlay({ projectID, ...options })
    }
  )

  const handleNewFloor = useEvent(
    (projectID: string, options?: NewFloorOptions) => {
      openNewFloorOverlay({ projectID, ...options })
    }
  )

  const handleShare = useEvent((projectID: string) => {
    openShareOverlay({ projectID })
  })

  const handleDuplicate = useEvent((projectID: string) => {
    openDuplicateProjectOverlay({ projectID })
  })

  const handleGaebExport = useEvent((projectID: string) => {
    openProjectGaebOverlay({ projectID })
  })

  const handleExport = useEvent((projectID: string) => {
    openProjectPdfOverlay({ projectID })
  })

  const handleMusterheftExport = useEvent((projectID: string) => {
    openMusterheftExportOverlay({ projectID })
  })

  const handleOpenAttachments = useEvent((projectID: string) => {
    openProjectAttachmentsOverlay({ projectID })
  })

  const handleActivateProject = useEvent((projectID: string) => {
    updateNeoletterProfile({ customFields: { active_project_id: projectID } })
  })

  const handleInviteMember = useEvent((projectID: string) => {
    openCreateProjectTeamMembersOverlay({ projectID })
  })

  const handleLeaveTeam = useEvent((projectID: string) => {
    openTeamLeaveOverlay({ projectID })
  })

  const handleManageTeam = useEvent((projectID: string) => {
    openManageProjectTeamOverlay({ projectID })
  })

  const handleOpenProjectHistory = useEvent((projectID: string) => {
    openProjectHistoryOverlay({ projectID })
  })

  const callbacks = useMemo(
    () => ({
      onOpen: handleOpenProject,
      onEdit: handleEditProject,
      onAddClarificationRequest: handleAddClarificationRequest,
      onDuplicate: handleDuplicate,
      onShare: handleShare,
      onDelete: handleDeleteProject,
      onExport: handleExport,
      onMusterheftExport: handleMusterheftExport,
      onGaebExport: handleGaebExport,
      onOpenAttachments: handleOpenAttachments,
      onActivate: handleActivateProject,
      onInviteMember: handleInviteMember,
      onLeaveTeam: handleLeaveTeam,
      onNewProduct: handleNewProductPosition,
      onNewRoom: handleNewRoom,
      onNewFloor: handleNewFloor,
      onManageTeam: handleManageTeam,
      onOpenProjectHistory: handleOpenProjectHistory
    }),
    [
      handleOpenProject,
      handleEditProject,
      handleDuplicate,
      handleShare,
      handleDeleteProject,
      handleExport,
      handleMusterheftExport,
      handleGaebExport,
      handleOpenAttachments,
      updateNeoletterProfile,
      handleNewProductPosition,
      handleNewRoom,
      handleNewFloor,
      handleInviteMember,
      handleLeaveTeam,
      handleManageTeam,
      handleOpenProjectHistory
    ]
  )

  return useEnhanceCallbacks({ callbacks, onAction })
}
